import { ButtonVariant } from "components/CoButton";
import useAskPaperDrawer from "features/AskPaperDrawer/hooks/useAskPaperDrawer";
import { useFeatureGate } from "features/Statsig";
import { FeatureFlag } from "features/Statsig/constants";
import { usePaperSidebarDrawer } from "features/PaperSidebarDrawer";
import { Paper } from "helpers/api";
import useLabels from "hooks/useLabels";
import { useAppDispatch } from "hooks/useStore";
import { useCallback } from "react";
import { addSource } from "store/slices/sources";
import CardTooltip from "../CardTooltip";
import { AskPaperIcon } from "design_system";

/**
 * @component AskPaperTagTooltip
 * @description Tooltip accompanying the "Ask paper" tag on a search result
 */

interface AskPaperTagTooltipProps {
  closeTooltip?: () => void;
  onClickAskPaper: () => void;
  paper: Paper;
  interactionId: string;
}

const AskPaperTagTooltip = ({
  closeTooltip,
  onClickAskPaper,
  paper,
  interactionId,
}: AskPaperTagTooltipProps) => {
  const [tooltipLabels] = useLabels("tooltips.ask-this-paper-tag");

  const isDataSourceSelectorEnabled = useFeatureGate(
    FeatureFlag.DATA_SOURCE_SELECTOR
  ).value;
  const { openAskPaperDrawer } = useAskPaperDrawer();
  const { openPaperSidebarDrawer } = usePaperSidebarDrawer();

  const dispatch = useAppDispatch();

  const handleAddSource = useCallback(
    (source: Paper) => {
      dispatch(addSource(source));
    },
    [dispatch]
  );

  const handleClick = (e: any) => {
    onClickAskPaper();
    if (isDataSourceSelectorEnabled) {
      handleAddSource(paper);
      openPaperSidebarDrawer(paper, interactionId);
    } else {
      openAskPaperDrawer({
        paperId: paper.paper_id,
        title: paper.title,
      });
    }
    e.preventDefault();
    if (closeTooltip) {
      closeTooltip();
    }
  };

  return (
    <CardTooltip>
      <CardTooltip.Header
        iconComponent={<AskPaperIcon size="lg" />}
        title={tooltipLabels["ask-paper"]}
        isBeta
      />
      <CardTooltip.Description content={tooltipLabels["description"]} />
      <CardTooltip.Button
        label={tooltipLabels["ask-this-paper"]}
        onClick={handleClick}
        variant={ButtonVariant.Tertiary}
      />
    </CardTooltip>
  );
};

export default AskPaperTagTooltip;
